// ConfirmDialog — переиспользуемое подтверждение действия поверх ResponsiveDialog
// (Dialog на desktop ≥640, Drawer на mobile). Используется для частично
// деструктивных действий: перегенерация готовых AI-материалов, и т.п.
//
// Текст кнопки подтверждения и описание — пропсами от вызывающего; tone
// "destructive" красит кнопку в destructive. pending — спиннер + disable.

import { Button } from "@studyhub/ui/components/button";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@studyhub/ui/components/responsive-dialog";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface ConfirmDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: ReactNode;
	description?: ReactNode;
	/** Текст основной кнопки (например «Перегенерировать»). */
	confirmLabel: string;
	/** Текст кнопки отмены (по умолчанию common.actions.cancel). */
	cancelLabel?: string;
	/** Иконка слева от текста основной кнопки. */
	confirmIcon?: ReactNode;
	tone?: "default" | "destructive";
	pending?: boolean;
	onConfirm: () => void;
}

export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmLabel,
	cancelLabel,
	confirmIcon,
	tone = "default",
	pending = false,
	onConfirm,
}: ConfirmDialogProps): React.ReactElement {
	const { t } = useTranslation("common");
	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent className="min-w-0 sm:max-w-md">
				<ResponsiveDialogHeader className="min-w-0 text-left">
					<ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
					{description ? (
						<ResponsiveDialogDescription>{description}</ResponsiveDialogDescription>
					) : null}
				</ResponsiveDialogHeader>
				<ResponsiveDialogFooter className="gap-2">
					<Button
						variant="outline"
						className="cursor-pointer"
						onClick={() => onOpenChange(false)}
						disabled={pending}
					>
						{cancelLabel ?? t("actions.cancel")}
					</Button>
					<Button
						variant={tone === "destructive" ? "destructive" : "default"}
						className="cursor-pointer gap-1.5"
						onClick={onConfirm}
						loading={pending}
					>
						{pending ? null : confirmIcon}
						{confirmLabel}
					</Button>
				</ResponsiveDialogFooter>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
